<template>
	<div
		class="user-assign-select-container-skeleton"
		:class="classAddons"
		:aria-label="$t('spa__general.loading')"
	>
		<SuiSkeleton
			v-if="isOwnerEnabled"
			data-testid="owner-skeleton"
			class="user-assign-select-container-skeleton__item"
			:width="circleSize"
			is-circle
			:animate
		/>
		<SuiIcon
			v-if="isOwnerEnabled"
			data-testid="separator-arrow"
			name="icon-arrow-right-16"
			class="user-assign-select-container-skeleton__icon"
		/>
		<SuiSkeleton
			v-for="index in skeletonItemsToShow"
			:key="index"
			data-testid="assignee-skeleton"
			class="user-assign-select-container-skeleton__item"
			:width="circleSize"
			is-circle
			:animate
		/>
	</div>
</template>

<script lang="ts" setup>
import { SuiIcon, SuiSkeleton } from '@developers/scoro-ui';
import { computed } from 'vue';
import {
	MAX_AVATARS_SHOWN_WITHOUT_HIDING,
	UserAssignSelectSize,
} from '../../constants';
import { type ValueOf } from '@/types';

export interface UserAssignSelectContainerSkeletonProps {
	usersCount?: number;
	maxAvatarsShown?: number;
	animate?: boolean;
	size?: ValueOf<typeof UserAssignSelectSize>;
	isOwnerEnabled?: boolean;
}

const props = withDefaults(
	defineProps<UserAssignSelectContainerSkeletonProps>(),
	{
		usersCount: 1,
		maxAvatarsShown: MAX_AVATARS_SHOWN_WITHOUT_HIDING,
		animate: true,
		size: UserAssignSelectSize.MEDIUM,
		isOwnerEnabled: true,
	}
);

const skeletonItemsToShow = computed(() =>
	Math.min(Math.max(props.usersCount, 1), props.maxAvatarsShown)
);

const circleSize = computed(() => {
	switch (props.size) {
		case UserAssignSelectSize.SMALL:
			return '2.5rem';
		case UserAssignSelectSize.LARGE:
			return '5rem';
		default:
			return '3.5rem';
	}
});

const classAddons = computed(() => ({
	[`user-assign-select-container-skeleton--${props.size}`]: true,
}));
</script>

<style lang="scss" scoped>
.user-assign-select-container-skeleton {
	$parent: &;

	display: flex;
	align-items: center;
	padding-left: 1rem;

	&__item + &__item {
		margin-left: -1rem;
	}

	&__icon {
		margin: 0 0.5rem;
	}

	&--small {
		gap: 0;

		#{$parent}__icon {
			margin: 0;
		}
	}

	&--large {
		gap: 1rem;
		padding-left: 0;

		#{$parent}__item {
			margin: 0;
		}

		#{$parent}__icon {
			margin: 0;
		}
	}
}
</style>
